import { useState, useEffect } from "react";
import { useGuidance } from "./GuidanceProvider";
import { useAuth } from "../../App";
import { X, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../ui/button";

export function WelcomeOverlay() {
  const { state, markWelcomeSeen } = useGuidance();
  const { household } = useAuth();
  const isViewer = household?.role === "viewer";
  const [show, setShow] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    if (state && !state.welcome_seen && !state.setup_complete && !isViewer) {
      setShow(true);
      requestAnimationFrame(() => setFadeIn(true));
    }
  }, [state]);

  if (!show) return null;

  const handleDismiss = () => {
    setFadeIn(false);
    setTimeout(() => {
      setShow(false);
      markWelcomeSeen();
    }, 300);
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
      data-testid="welcome-overlay"
    >
      <div className={`relative bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 transition-all duration-500 ${fadeIn ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="welcome-dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <h2 className="font-heading text-2xl font-semibold" data-testid="welcome-title">
            Let's get you set up
          </h2>
        </div>

        <p className="text-muted-foreground mb-4 leading-relaxed">
          We'll walk through a few simple steps — one at a time. No rush, no pressure. Just getting the basics in place so everything works for you.
        </p>

        <div className="space-y-3 mb-6">
          {state?.steps && Object.entries(state.steps).map(([key, step]) => (
            <div
              key={key}
              className="flex items-center gap-3 text-sm"
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                step.done
                  ? 'bg-primary/15 text-primary'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {step.done ? '✓' : Object.keys(state.steps).indexOf(key) + 1}
              </div>
              <span className={step.done ? 'text-muted-foreground line-through' : 'text-foreground'}>
                {step.label}
              </span>
            </div>
          ))}
        </div>

        <Button onClick={handleDismiss} className="w-full btn-primary" data-testid="welcome-start-btn">
          Let's go
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
